/**
 * Inspeção da devolução — checklist do dono (spec-inspecao, 2026-09-23) e
 * sugestão de destino para o SUPERVISOR (ajuste 2 do dono).
 *
 * O ASSISTENTE termina no checklist: série + "é o nosso?" + 4 etapas
 * (transporte, embalagem do produto, componentes, produto), cada uma com a
 * resposta e as fotos da etapa. O operador NÃO vê destino nem orientação —
 * quem define o destino (ESTOQUE / RMA / DESCARTE, `INSPECTION_DESTINATIONS`
 * em `pedidoTags.js`) é o supervisor (`rmas:resolve`), no caso, com a
 * orientação da tabela checklist × motivo como sugestão.
 *
 * Valores do checklist = contrato wire (colunas `inspection_*` da migração
 * 0041, snake_case). A tabela de orientação é a MESMA do backend
 * (`InspectionGuidance` em modules/rma): o front usa o que o DTO trouxer
 * (`inspection_guidance`) e só calcula aqui quando o caso vier sem ela.
 */

// ── Etapas do checklist ─────────────────────────────────────────────────────

/**
 * As 4 etapas com foto, na ordem do assistente. `field` = coluna/wire;
 * `ok` = resposta "sem problema"; `problem` = respostas que exigem o mínimo
 * de fotos de problema (todos os lados); `note` = rótulo da foto no evento
 * ANEXO do caso.
 */
export const INSPECTION_CHECK_STEPS = [
    {
        key: 'transporte',
        field: 'transport_packaging',
        title: 'Embalagem de transporte',
        question: 'A embalagem de TRANSPORTE chegou…',
        options: [
            { value: 'ok', label: 'Íntegra' },
            { value: 'avariada', label: 'Avariada' },
        ],
        ok: 'ok',
        problem: ['avariada'],
        basePhotos: 1,
        photoHint: 'A caixa em que a devolução chegou, como veio.',
        problemHint: 'Avariada: no mínimo 4 fotos — todos os lados.',
        note: 'inspeção: embalagem de transporte',
    },
    {
        key: 'embalagem',
        field: 'product_packaging',
        title: 'Embalagem do produto',
        question: 'A embalagem do PRODUTO chegou…',
        options: [
            { value: 'ok', label: 'Íntegra' },
            { value: 'avariada', label: 'Avariada' },
            { value: 'ausente', label: 'Ausente' },
        ],
        ok: 'ok',
        problem: ['avariada', 'ausente'],
        basePhotos: 1,
        photoHint: 'A caixa original do produto.',
        problemHint: 'Avariada ou ausente: no mínimo 4 fotos — todos os lados.',
        absentHint: 'Ausente: fotografe o produto sem a embalagem.',
        note: 'inspeção: embalagem do produto',
    },
    {
        key: 'componentes',
        field: 'components',
        title: 'Componentes',
        question: 'Os COMPONENTES internos estão…',
        options: [
            { value: 'completos', label: 'Todos presentes' },
            { value: 'faltando', label: 'Faltando' },
        ],
        ok: 'completos',
        problem: ['faltando'],
        basePhotos: 1,
        photoHint: 'Tudo o que veio dentro da caixa, à mostra.',
        problemHint: 'Faltando: no mínimo 4 fotos — todos os lados.',
        note: 'inspeção: componentes',
    },
    {
        key: 'produto',
        field: 'product_condition',
        title: 'Produto',
        question: 'O PRODUTO apresenta…',
        options: [
            { value: 'ok', label: 'Sem marcas' },
            { value: 'marcas_uso', label: 'Marcas de uso' },
            { value: 'dano', label: 'Dano visível' },
        ],
        ok: 'ok',
        problem: ['marcas_uso', 'dano'],
        basePhotos: 0,
        photoHint: 'Sem marcas nem dano: foto opcional.',
        problemHint: 'Marcas de uso ou dano: no mínimo 4 fotos — todos os lados.',
        note: 'inspeção: produto',
    },
];

/** Chaves das etapas com foto, na ordem. */
export const INSPECTION_STEP_KEYS = INSPECTION_CHECK_STEPS.map((step) => step.key);

/** Mínimo de fotos de uma etapa com problema (dono: "de todos os lados"). */
export const INSPECTION_PROBLEM_PHOTOS = 4;

/** Teto de fotos por etapa no assistente. */
export const INSPECTION_MAX_PHOTOS_PER_STEP = 10;

/** Meta de uma etapa pela chave (`transporte`…); desconhecida → null. */
export function InspectionStepMeta(stepKey) {
    return INSPECTION_CHECK_STEPS.find((step) => step.key === stepKey) || null;
}

/**
 * Mínimo de fotos da etapa para a resposta dada — regra do dono: 1 foto
 * obrigatória (transporte, embalagem, componentes); com problema, 4 (todos os
 * lados); produto sem marcas não exige foto. Sem resposta ainda: o mínimo
 * base (a foto obrigatória vale para qualquer resposta).
 */
export function InspectionPhotoMinimum(stepKey, answer) {
    const step = InspectionStepMeta(stepKey);
    if (!step) {
        return 0;
    }
    return step.problem.includes(answer) ? INSPECTION_PROBLEM_PHOTOS : step.basePhotos;
}

/** A resposta da etapa é "sem problema"? (sem resposta → null) */
export function IsStepOk(stepKey, answer) {
    const step = InspectionStepMeta(stepKey);
    if (!step || !answer) {
        return null;
    }
    return answer === step.ok;
}

/** Rótulo da resposta de uma etapa (valor desconhecido volta cru). */
export function InspectionAnswerLabel(stepKey, answer) {
    const step = InspectionStepMeta(stepKey);
    const option = step?.options.find((opt) => opt.value === answer);
    return option ? option.label : answer || '—';
}

// ── Checklist do caso (DTO) ──────────────────────────────────────────────────

/**
 * Checklist gravado no caso (colunas `inspection_*` da 0041) → objeto com os
 * nomes do wire do POST. Caso sem checklist (inspeção antiga, só
 * `inspection_issue`/`inspection_damage`) → null.
 */
export function ChecklistFromRma(rma) {
    if (!rma) {
        return null;
    }
    const checklist = {
        is_our_product: typeof rma.inspection_is_our_product === 'boolean' ? rma.inspection_is_our_product : null,
        transport_packaging: rma.inspection_transport_packaging || null,
        product_packaging: rma.inspection_product_packaging || null,
        components: rma.inspection_components || null,
        missing_components: rma.inspection_missing_components || null,
        product_condition: rma.inspection_product_condition || null,
    };
    const answered =
        checklist.is_our_product !== null || INSPECTION_CHECK_STEPS.some((step) => checklist[step.field] !== null);
    return answered ? checklist : null;
}

/**
 * Linhas ✓/⚠ do checklist (tela final do assistente e caso): "é o nosso?" e
 * as 4 etapas. `ok` null = sem resposta.
 */
export function ChecklistLines(checklist) {
    if (!checklist) {
        return [];
    }
    const ours = checklist.is_our_product;
    const lines = [
        {
            key: 'nosso',
            label: 'É o nosso produto',
            ok: ours === null || ours === undefined ? null : ours === true,
            value: ours === true ? 'sim' : ours === false ? 'NÃO — produto trocado' : '—',
        },
    ];
    for (const step of INSPECTION_CHECK_STEPS) {
        const answer = checklist[step.field];
        let value = InspectionAnswerLabel(step.key, answer).toLowerCase();
        if (step.key === 'componentes' && answer === 'faltando' && checklist.missing_components) {
            value = `faltando: ${checklist.missing_components}`;
        }
        lines.push({ key: step.key, label: step.title, ok: IsStepOk(step.key, answer), value });
    }
    return lines;
}

/** Quantos itens do checklist deram problema (⚠). */
export function ChecklistProblemCount(checklist) {
    return ChecklistLines(checklist).filter((line) => line.ok === false).length;
}

// ── Fotos por etapa (eventos ANEXO do caso) ──────────────────────────────────

function foldText(value) {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}

/** Grupo das fotos da inspeção antiga (sem etapa marcada). */
export const INSPECTION_PHOTO_UNSTAGED = 'sem_etapa';

// Nota FIXA do ANEXO gravado pela inspeção ANTIGA (sem checklist —
// RmaService.registerInspection, corpo antigo).
const LEGACY_INSPECTION_PHOTO_NOTE = 'Foto da inspeção';

/**
 * Etapa da foto a partir do evento ANEXO: `inspection_step` do wire (o
 * backend lê da nota fixa da etapa; null = não é foto de etapa). Foto da
 * inspeção antiga (nota exata "Foto da inspeção") → `sem_etapa`; anexo
 * avulso → null.
 */
export function InspectionPhotoStep(event) {
    const step = event?.inspection_step ?? null;
    if (step && INSPECTION_STEP_KEYS.includes(step)) {
        return step;
    }
    return event?.note === LEGACY_INSPECTION_PHOTO_NOTE ? INSPECTION_PHOTO_UNSTAGED : null;
}

/**
 * Anexos do caso agrupados por etapa: `{ transporte: [...], …, sem_etapa:
 * [...] }` + `outros` (anexos que não são da inspeção).
 */
export function GroupInspectionPhotos(attachments) {
    const groups = { outros: [] };
    for (const key of [...INSPECTION_STEP_KEYS, INSPECTION_PHOTO_UNSTAGED]) {
        groups[key] = [];
    }
    for (const attachment of attachments || []) {
        const step = InspectionPhotoStep(attachment);
        groups[step || 'outros'].push(attachment);
    }
    return groups;
}

// ── Motivo da devolução (espelho de RETURN_REASON_GROUPS do backend) ─────────

/**
 * Espelho de `RETURN_REASON_GROUPS` (backend_v2/src/modules/rma/mlClaimCatalog.js)
 * — só para a SUGESTÃO do supervisor quando o DTO não traz o grupo. Mesma
 * precedência: o caso pertence ao PRIMEIRO grupo que casar (reason_id
 * conhecido ou palavra-chave em ml_problem + reason_detail, sem caixa nem
 * acento, como o LIKE do MariaDB); nenhum → `outros`.
 */
export const RETURN_REASON_GROUPS = [
    {
        key: 'defeito',
        label: 'Com defeito',
        reasonIds: [],
        keywords: ['defeito', 'não funciona', 'nao funciona', 'não liga', 'parou de funcionar'],
    },
    {
        key: 'avaria',
        label: 'Avaria / embalagem',
        reasonIds: ['PDD9812'],
        keywords: ['quebrado', 'arranhado', 'danificad', 'avaria', 'amassad', 'rasgad', 'vazand'],
    },
    {
        key: 'produto_diferente',
        label: 'Produto diferente',
        reasonIds: ['PDD9942', 'PDD9943', 'PDD9944', 'PDD9970'],
        keywords: ['diferente', 'outro tipo de produto', 'marca original', 'descumpre', 'tamanho, sabor ou marca'],
    },
    {
        key: 'incompleto',
        label: 'Incompleto / faltando',
        reasonIds: ['PDD9825'],
        keywords: ['faltam', 'faltando', 'incomplet', 'caixa vazia'],
    },
    {
        key: 'arrependimento',
        label: 'Arrependimento',
        reasonIds: ['PDD9939', 'PNR9509', 'PNR9501', 'PDD9502'],
        keywords: ['arrependi', 'não a quero', 'nao quero mais', 'não quero mais'],
    },
    {
        key: 'nao_recebido',
        label: 'Não recebido',
        reasonIds: ['PNR9560'],
        keywords: ['não recebi', 'nao recebi'],
    },
];

/** Grupo do motivo da devolução do caso (`reason_group` do wire, se vier). */
export function ReturnReasonGroup(rma) {
    if (rma?.reason_group) {
        return rma.reason_group;
    }
    const text = foldText([rma?.ml_problem, rma?.reason_detail].filter(Boolean).join(' '));
    for (const group of RETURN_REASON_GROUPS) {
        if (rma?.reason_id && group.reasonIds.includes(rma.reason_id)) {
            return group.key;
        }
        if (group.keywords.some((keyword) => text.includes(foldText(keyword)))) {
            return group.key;
        }
    }
    return 'outros';
}

/** Rótulo do grupo do motivo (`outros` → "Outros motivos"). */
export function ReturnReasonGroupLabel(groupKey) {
    return RETURN_REASON_GROUPS.find((group) => group.key === groupKey)?.label || 'Outros motivos';
}

// ── Orientação (checklist × motivo) → sugestão do supervisor ─────────────────

/**
 * Pendências que a orientação pede ao supervisor. "Aprovar/contestar no ML"
 * é INSTRUÇÃO para fazer no painel do ML — o sistema nunca escreve no ML.
 */
export const GUIDANCE_ACTION_LABELS = {
    aprovar_ml: 'Aprovar a devolução no painel do ML',
    contestar_ml: 'Contestar a devolução no painel do ML',
    nf_perda: 'Emitir NF de perda',
    testar: 'Testar o produto',
    avaria_transportadora: 'Registrar avaria da transportadora',
    repack: 'Reembalar (repack)',
};

/**
 * A tabela do dono (spec-inspecao, "Orientação"), por código. `destination`
 * = destino sugerido (ESTOQUE | RMA | DESCARTE) — `componentes_faltando`
 * depende do estado do produto (ver `GuidanceDestination`).
 */
export const INSPECTION_GUIDANCE = {
    reestoque_aprovar_ml: {
        label: 'Reestoque como novo; no ML, aprovar a devolução',
        destination: 'ESTOQUE',
        actions: ['aprovar_ml'],
    },
    testar_produto: {
        label: 'Testar: funcionou → reestoque; não funcionou → RMA fabricante',
        destination: 'ESTOQUE',
        actions: ['testar'],
    },
    reestoque_contestar_ml: {
        label: 'Reestoque; no ML, contestar — o produto que voltou é o nosso e está perfeito',
        destination: 'ESTOQUE',
        actions: ['contestar_ml'],
    },
    reestoque_avaria_transporte: {
        label: 'Reestoque como novo; registrar a avaria da transportadora',
        destination: 'ESTOQUE',
        actions: ['avaria_transportadora'],
    },
    repack_reestoque: {
        label: 'Repack (reembalar) e reestoque — alternativa: embalagem avariada/outlet',
        destination: 'ESTOQUE',
        actions: ['repack'],
    },
    componentes_faltando: {
        label: 'Contestar no ML (incompleto) + NF de perda do que faltou; produto → repack ou fabricante',
        destination: 'ESTOQUE',
        actions: ['contestar_ml', 'nf_perda'],
    },
    contestar_sinais_uso: {
        label: 'Contestar no ML (sinais de uso) + NF de perda se o ML não cobrir',
        destination: 'DESCARTE',
        actions: ['contestar_ml', 'nf_perda'],
    },
    rma_fabricante: {
        label: 'RMA fabricante (garantia) se compatível; senão, contestar no ML',
        destination: 'RMA',
        actions: [],
    },
    produto_trocado: {
        label: 'Contestar no ML (produto trocado) + NF de perda',
        destination: 'DESCARTE',
        actions: ['contestar_ml', 'nf_perda'],
    },
};

const DEFECT_REASON_GROUPS = ['defeito', 'avaria'];
// Motivo que o checklist "tudo ok" DESMENTE (o produto voltou perfeito):
// contestar no ML em vez de aprovar.
const CONTESTED_REASON_GROUPS = ['produto_diferente', 'avaria', 'incompleto'];

/**
 * Código da orientação para checklist × grupo do motivo — espelho de
 * `InspectionGuidance` do backend (precedência: produto trocado →
 * componentes faltando → marcas/dano → embalagem do produto → transporte →
 * tudo ok). Checklist sem resposta → null.
 */
export function InspectionGuidanceCode(checklist, reasonGroup) {
    if (!checklist) {
        return null;
    }
    const damaged = ['marcas_uso', 'dano'].includes(checklist.product_condition);
    if (checklist.is_our_product === false) {
        return 'produto_trocado';
    }
    if (checklist.components === 'faltando') {
        return 'componentes_faltando';
    }
    if (damaged) {
        return DEFECT_REASON_GROUPS.includes(reasonGroup) ? 'rma_fabricante' : 'contestar_sinais_uso';
    }
    if (['avariada', 'ausente'].includes(checklist.product_packaging)) {
        return 'repack_reestoque';
    }
    if (checklist.transport_packaging === 'avariada') {
        return 'reestoque_avaria_transporte';
    }
    if (reasonGroup === 'defeito') {
        return 'testar_produto';
    }
    if (CONTESTED_REASON_GROUPS.includes(reasonGroup)) {
        return 'reestoque_contestar_ml';
    }
    return 'reestoque_aprovar_ml';
}

/**
 * Destino sugerido pela orientação. Contestação é conforme o ESTADO do
 * produto (ajuste do dono): com marcas de uso ou dano, padrão DESCARTE;
 * componentes faltando com o produto perfeito → ESTOQUE (repack).
 */
export function GuidanceDestination(code, checklist) {
    if (code === 'componentes_faltando') {
        return ['marcas_uso', 'dano'].includes(checklist?.product_condition) ? 'DESCARTE' : 'ESTOQUE';
    }
    return INSPECTION_GUIDANCE[code]?.destination || null;
}

// Inspeção antiga (sem checklist): o desfecho sugerido de antes vira destino.
const RESOLUTION_TO_DESTINATION = {
    REESTOQUE: 'ESTOQUE',
    REPARO: 'ESTOQUE',
    DEVOLUCAO_FORNECEDOR: 'RMA',
    GARANTIA_FABRICANTE: 'RMA',
    DESCARTE: 'DESCARTE',
};

/**
 * Sugestão para o bloco "Definir destino": `{ code, label, destination,
 * actions, reasonGroup, legacy }`. Ordem das fontes: o que o DTO trouxer
 * (`inspection_guidance` = `{ code, label, destination, actions }` ou null;
 * `inspection_suggested_destination`) → a tabela daqui sobre o checklist do
 * caso → inspeção antiga (`suggested_resolution`). Sem nada → null.
 */
export function InspectionSuggestion(rma) {
    if (!rma) {
        return null;
    }
    const checklist = ChecklistFromRma(rma);
    const reasonGroup = ReturnReasonGroup(rma);
    const wire = rma.inspection_guidance || null;
    const code = wire?.code || InspectionGuidanceCode(checklist, reasonGroup);
    if (code) {
        const meta = INSPECTION_GUIDANCE[code];
        return {
            code,
            label: wire?.label || meta?.label || code,
            destination:
                wire?.destination ||
                rma.inspection_suggested_destination ||
                GuidanceDestination(code, checklist) ||
                null,
            actions: wire?.actions || meta?.actions || [],
            reasonGroup,
            legacy: false,
        };
    }
    const legacyDestination =
        rma.inspection_suggested_destination || RESOLUTION_TO_DESTINATION[rma.suggested_resolution] || null;
    if (!legacyDestination) {
        return null;
    }
    return {
        code: null,
        label: `Inspeção sem checklist: conferência ${rma.inspection_issue ? 'com problema' : 'OK'} · ${
            rma.inspection_damage ? 'com avaria' : 'sem avaria'
        }`,
        destination: legacyDestination,
        actions: rma.inspection_issue ? ['contestar_ml', 'nf_perda'] : [],
        reasonGroup,
        legacy: true,
    };
}

// ── Destino → desfecho (execução reusa o "Resolver" existente) ───────────────

/**
 * Desfechos (`resolution` do POST /rmas/:id/status → RESOLVIDO) que EXECUTAM
 * cada destino: ESTOQUE = reestoque; RMA = devolução ao fornecedor ou
 * garantia (entra como candidato ao lote de RMAs); DESCARTE = descarte.
 */
export const DESTINATION_RESOLUTIONS = {
    ESTOQUE: ['REESTOQUE'],
    RMA: ['DEVOLUCAO_FORNECEDOR', 'GARANTIA_FABRICANTE'],
    DESCARTE: ['DESCARTE'],
};

/** Texto de apoio de cada destino nos botões do supervisor. */
export const DESTINATION_HINTS = {
    ESTOQUE: 'volta ao estoque (inclui reembalar)',
    RMA: 'fabricante — entra como candidato ao lote de RMAs',
    DESCARTE: 'descarte — gera a NF de perda',
};
